import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { masterJobsReadApi } from "../config/masterJobsApi";
import {
  OG_IMAGE_URL,
  buildCanonicalUrl,
  buildJobBadgeItems,
  buildJobFaqEntries,
  buildJobMetaDescription,
  buildJobSeoMeta,
  buildJobSeoTitle,
  buildJobStructuredData,
  detectJobStatus,
  getJobSlug,
  getJobStatusLabel,
} from "../config/publicPageContent";
import { getPageType, trackCtaClick } from "../../utils/analytics";
import "./post.css";

const HEADER_KEYS = [
  "title",
  "advertisement_no",
  "advertisementNo",
  "post_name",
  "postName",
  "short_description",
  "shortDescription",
];

const SECTION_ALIASES = {
  short_description: "shortDescription",
  short_description_text: "shortDescription",
  description: "description",
  important_dates: "importantDates",
  important_dates_table: "importantDates",
  application_fee: "applicationFee",
  eligibility: "eligibilityCriteria",
  eligibility_criteria: "eligibilityCriteria",
  vacancy_detail: "vacancyDetails",
  vacancy_details: "vacancyDetails",
  selection_process: "selectionProcess",
  application_process: "applicationProcess",
  official_links: "officialLinks",
  important_links: "officialLinks",
  syllabus_overview: "syllabusOverview",
  exam_scheme: "examScheme",
  important_notes: "importantNotes",
  other_tables: "otherTables",
  pay_scale: "payScale",
  conducting_body: "conductingBody",
  source_link: "source",
};

const SECTION_PRIORITY = [
  "shortDescription",
  "description",
  "importantDates",
  "applicationFee",
  "eligibilityCriteria",
  "vacancyDetails",
  "selectionProcess",
  "applicationProcess",
  "importantNotes",
  "examScheme",
  "otherTables",
];

const LAST_SECTIONS = ["officialLinks", "syllabusOverview", "source"];

const normalizeKey = (key) =>
  String(key || "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();

const canonicalKey = (key) => SECTION_ALIASES[normalizeKey(key)] || String(key || "");

const isObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isPrimitive = (value) =>
  value == null || ["string", "number", "boolean"].includes(typeof value);

const isUrl = (value) => typeof value === "string" && /^(https?:)?\/\//i.test(value);

const isEmptyValue = (value) => {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.every(isEmptyValue);
  if (isObject(value)) return Object.values(value).every(isEmptyValue);
  return false;
};

const formatLabel = (key) =>
  String(key ?? "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\./g, " -> ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getOrderedEntries = (obj) =>
  Object.entries(obj || {}).sort((left, right) => {
    const leftEmpty = isEmptyValue(left[1]);
    const rightEmpty = isEmptyValue(right[1]);
    if (leftEmpty === rightEmpty) return 0;
    return leftEmpty ? 1 : -1;
  });

const cleanText = (value) => String(value || "").replace(/\s+/g, " ").trim();

const truncateText = (value, maxLength = 220) => {
  const text = cleanText(value);
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
};

const findSectionValue = (data, aliases = []) => {
  const normalizedAliases = aliases.map((key) => canonicalKey(key));
  const entry = Object.entries(data || {}).find(([key, value]) => {
    if (isEmptyValue(value)) return false;
    return normalizedAliases.includes(canonicalKey(key));
  });
  return entry ? entry[1] : null;
};

const summarizeValue = (value) => {
  if (isEmptyValue(value)) return "";
  if (typeof value === "string") return truncateText(value);
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const firstUseful = value.find((item) => !isEmptyValue(item));
    return summarizeValue(firstUseful);
  }
  if (isObject(value)) {
    const firstUseful = Object.values(value).find((item) => !isEmptyValue(item));
    return summarizeValue(firstUseful);
  }
  return "";
};

const buildJobSupportContent = (data, title, postName) => {
  const importantDates = summarizeValue(findSectionValue(data, ["importantDates"]));
  const eligibility = summarizeValue(findSectionValue(data, ["eligibilityCriteria"]));
  const selectionProcess = summarizeValue(findSectionValue(data, ["selectionProcess"]));
  const applicationProcess = summarizeValue(findSectionValue(data, ["applicationProcess"]));
  const applicationFee = summarizeValue(findSectionValue(data, ["applicationFee"]));
  const vacancyDetails = summarizeValue(findSectionValue(data, ["vacancyDetails"]));
  const payScale = summarizeValue(findSectionValue(data, ["payScale"]));

  const overviewPoints = [
    `${title} is listed here in a structured format so candidates can review the main recruitment details more quickly before visiting the official notice.`,
    importantDates
      ? `Important dates summary: ${importantDates}`
      : "Candidates should check the important dates section carefully before planning the application.",
    eligibility
      ? `Eligibility snapshot: ${eligibility}`
      : `Applicants should review the eligibility requirements carefully for the ${postName || "post"}.`,
  ];

  const checklist = [
    "Read the official notification completely before applying.",
    "Verify eligibility, document requirements, and application dates.",
    "Keep registration details, fee receipts, and downloaded notices saved for future reference.",
  ];

  const reminders = [
    applicationFee
      ? `Application fee details are available in the post content: ${applicationFee}`
      : "Check whether the application process includes any fee, category-wise exemption, or payment instructions.",
    selectionProcess
      ? `Selection process summary: ${selectionProcess}`
      : "Review the selection stages carefully so you know whether the recruitment includes written tests, interviews, or document verification.",
    applicationProcess
      ? `Application process note: ${applicationProcess}`
      : "Do not wait until the last day to apply, because official portals may become slow near deadlines.",
  ];

  const faq = [
    {
      question: `What should candidates check first in the ${postName || title} post?`,
      answer:
        "Candidates should start with eligibility, important dates, application mode, and the official notification link before preparing any application.",
    },
    {
      question: "Why is this page useful if the official notice already exists?",
      answer:
        "This page organizes the recruitment details into readable sections so users can understand the post faster, but the official notice should still be treated as the final source.",
    },
    {
      question: "What should I do after reading the job post?",
      answer:
        "After reading the post, verify the official notification, confirm your eligibility, prepare your documents, and note the last date so you do not miss the application window.",
    },
  ];

  if (vacancyDetails) {
    faq.push({
      question: "Does this post include vacancy information?",
      answer: `Yes. Vacancy-related details available on this page include: ${vacancyDetails}`,
    });
  }

  if (payScale) {
    faq.push({
      question: "Is salary or pay scale information available?",
      answer: `Yes. The available pay-related information is: ${payScale}`,
    });
  }

  return { overviewPoints, checklist, reminders, faq };
};

export const normalizeJobPayload = (job) => {
  const parsedContent =
    typeof job?.content === "string"
      ? JSON.parse(job.content)
      : job?.content;

  if (parsedContent && isObject(parsedContent)) {
    return { ...job, ...parsedContent };
  }

  return isObject(job) ? job : {};
};

export const buildOrderedSections = (data) => {
  const remainingEntries = getOrderedEntries(data).filter(
    ([key, value]) => !HEADER_KEYS.includes(key) && !isEmptyValue(value)
  );

  const sectionMap = new Map(
    remainingEntries.map(([key, value]) => [canonicalKey(key), [key, value]])
  );

  const ordered = [];

  SECTION_PRIORITY.forEach((key) => {
    if (!sectionMap.has(key)) return;
    ordered.push(sectionMap.get(key));
    sectionMap.delete(key);
  });

  [...sectionMap.entries()].forEach(([key, value]) => {
    if (LAST_SECTIONS.includes(key)) return;
    ordered.push(value);
    sectionMap.delete(key);
  });

  LAST_SECTIONS.forEach((key) => {
    if (!sectionMap.has(key)) return;
    ordered.push(sectionMap.get(key));
    sectionMap.delete(key);
  });

  return ordered;
};

const getObjectColumns = (rows) =>
  Array.from(
    new Set(
      rows.flatMap((row) => (isObject(row) ? Object.keys(row).filter((key) => !isEmptyValue(row[key])) : []))
    )
  );

const formatScalar = (value) => {
  if (value == null || value === "") return "NA";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const flattenPrimitiveRows = (value, parentKey = "") => {
  if (isEmptyValue(value)) return [];

  if (isPrimitive(value)) {
    return [[parentKey || "details", value]];
  }

  if (Array.isArray(value)) {
    if (value.every(isPrimitive)) {
      return [[parentKey || "details", value.filter((item) => !isEmptyValue(item)).join(", ")]];
    }

    return value.flatMap((item, index) =>
      flattenPrimitiveRows(item, parentKey ? `${parentKey}_${index + 1}` : `item_${index + 1}`)
    );
  }

  if (isObject(value)) {
    return getOrderedEntries(value)
      .filter(([, child]) => !isEmptyValue(child))
      .flatMap(([key, child]) =>
        flattenPrimitiveRows(child, parentKey ? `${parentKey}_${key}` : key)
      );
  }

  return [];
};

const getSingleObjectEntry = (value) => {
  if (!isObject(value)) return null;
  const entries = getOrderedEntries(value).filter(([, child]) => !isEmptyValue(child));
  return entries.length === 1 ? entries[0] : null;
};

function LinkValue({ value, fieldKey, label, analyticsContext = {} }) {
  const href = isUrl(value) ? value : `https://${String(value).replace(/^\/+/, "")}`;
  const labelMap = {
    apply_online: "Apply Online",
    official_website: "Official Website",
    check_official_notification: "Official Notification",
    notification_pdf: "Download Notification",
    result_link: "Download Result",
    source: "Source Link",
  };
  const resolvedLabel = labelMap[normalizeKey(fieldKey)] || label || formatLabel(fieldKey) || "Open Link";

  const handleClick = () => {
    trackCtaClick({
      fieldKey,
      label: resolvedLabel,
      destinationUrl: href,
      page_type: analyticsContext.pageType || getPageType(window.location.pathname),
      item_title: analyticsContext.itemTitle || "",
      exam_name: analyticsContext.examName || "",
      organization_name: analyticsContext.organizationName || "",
      post_name: analyticsContext.postName || "",
      category_type: analyticsContext.categoryType || "",
      source_section: analyticsContext.sourceSection || "",
    });
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="link-btn"
      onClick={handleClick}
    >
      {resolvedLabel}
    </a>
  );
}

function PrimitiveValue({ value, fieldKey, analyticsContext }) {
  if (typeof value === "string" && isUrl(value)) {
    return <LinkValue value={value} fieldKey={fieldKey} analyticsContext={analyticsContext} />;
  }

  return <span>{formatScalar(value)}</span>;
}

function InlineKeyValue({ label, value, fieldKey, analyticsContext }) {
  return (
    <div className="job-inline-row">
      <strong>{formatLabel(label)}:</strong>{" "}
      <ValueRenderer value={value} fieldKey={fieldKey} compact analyticsContext={analyticsContext} />
    </div>
  );
}

function KeyValueTable({ data, analyticsContext }) {
  const rows = getOrderedEntries(data).filter(([, value]) => !isEmptyValue(value));
  if (!rows.length) return null;

  return (
    <div className="job-table-wrapper">
      <table className="job-table">
        <tbody>
          {rows.map(([key, value]) => (
            <tr key={key}>
              <th>{formatLabel(key)}</th>
              <td>
                <ValueRenderer value={value} fieldKey={key} analyticsContext={analyticsContext} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FlatKeyValueTable({ data, analyticsContext }) {
  const rows = flattenPrimitiveRows(data).filter(([, value]) => !isEmptyValue(value));
  if (!rows.length) return null;

  return (
    <div className="job-table-wrapper">
      <table className="job-table">
        <tbody>
          {rows.map(([key, value], index) => (
            <tr key={`${key}-${index}`}>
              <th>{formatLabel(key)}</th>
              <td>
                <PrimitiveValue value={value} fieldKey={key} analyticsContext={analyticsContext} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ObjectRowsTable({ title, rows, analyticsContext }) {
  const filteredRows = rows.filter((row) => isObject(row) && !isEmptyValue(row));
  const columns = getObjectColumns(filteredRows);

  if (!filteredRows.length) return null;

  return (
    <div className="job-subsection">
      {title ? <h4>{formatLabel(title)}</h4> : null}
      <div className="job-table-wrapper">
        <table className="job-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{formatLabel(column)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row, index) => (
              <tr key={`${title || "row"}-${index}`}>
                {columns.map((column) => (
                  <td key={column}>
                    <ValueRenderer
                      value={row[column]}
                      fieldKey={column}
                      analyticsContext={analyticsContext}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BulletList({ items, fieldKey, analyticsContext }) {
  const filtered = items.filter((item) => !isEmptyValue(item));
  if (!filtered.length) return null;

  return (
    <ul className="bullet-list">
      {filtered.map((item, index) => (
        <li key={`${fieldKey || "item"}-${index}`}>
          <ValueRenderer
            value={item}
            fieldKey={fieldKey}
            compact
            analyticsContext={analyticsContext}
          />
        </li>
      ))}
    </ul>
  );
}

function ValueRenderer({ value, fieldKey = "", compact = false, analyticsContext }) {
  if (isEmptyValue(value)) return <span>NA</span>;

  if (isPrimitive(value)) {
    return <PrimitiveValue value={value} fieldKey={fieldKey} analyticsContext={analyticsContext} />;
  }

  if (Array.isArray(value)) {
    if (value.length === 1 && !isEmptyValue(value[0])) {
      const singleItem = value[0];

      if (isPrimitive(singleItem)) {
        return (
          <PrimitiveValue
            value={singleItem}
            fieldKey={fieldKey}
            analyticsContext={analyticsContext}
          />
        );
      }

      const singleEntry = getSingleObjectEntry(singleItem);
      if (singleEntry) {
        const [childKey, childValue] = singleEntry;
        return (
          <InlineKeyValue
            label={childKey}
            value={childValue}
            fieldKey={childKey}
            analyticsContext={analyticsContext}
          />
        );
      }
    }

    const objectItems = value.filter((item) => isObject(item));
    if (objectItems.length === value.length) {
      return <ObjectRowsTable rows={objectItems} analyticsContext={analyticsContext} />;
    }
    return <BulletList items={value} fieldKey={fieldKey} analyticsContext={analyticsContext} />;
  }

  if (isObject(value)) {
    const entries = getOrderedEntries(value).filter(([, child]) => !isEmptyValue(child));
    if (entries.length === 1) {
      const [childKey, childValue] = entries[0];
      return (
        <InlineKeyValue
          label={childKey}
          value={childValue}
          fieldKey={childKey}
          analyticsContext={analyticsContext}
        />
      );
    }

    const allArraysOfObjects =
      entries.length > 0 &&
      entries.every(([, child]) => Array.isArray(child) && child.every((item) => isObject(item)));

    if (allArraysOfObjects) {
      return (
        <div>
          {entries.map(([key, child]) => (
            <ObjectRowsTable
              key={key}
              title={key}
              rows={child}
              analyticsContext={analyticsContext}
            />
          ))}
        </div>
      );
    }

    const primitiveLikeEntries = entries.filter(
      ([, child]) => isPrimitive(child) || (Array.isArray(child) && child.every(isPrimitive))
    );

    if (primitiveLikeEntries.length === entries.length || compact) {
      return <KeyValueTable data={value} analyticsContext={analyticsContext} />;
    }

    return (
      <div className="job-nested-stack">
        {entries.map(([key, child]) => (
          <SectionBlock
            key={key}
            title={key}
            value={child}
            nested
            analyticsContext={analyticsContext}
          />
        ))}
      </div>
    );
  }

  return <span>{String(value)}</span>;
}

function SectionBlock({ title, value, nested = false, analyticsContext }) {
  if (isEmptyValue(value)) return null;

  const sectionClass = nested ? "job-section nested job-subsection" : "job-section nested";
  const canonicalTitle = canonicalKey(title);
  const titleNode = nested ? (
    <h4 className="job-subsection-title">{formatLabel(title)}</h4>
  ) : (
      <div className="section-title">
        <span className="red-line" />
        {formatLabel(title)}
      </div>
    );

  if (!nested && canonicalTitle === "officialLinks" && isObject(value)) {
    return (
      <div className={sectionClass}>
        {titleNode}
        <FlatKeyValueTable
          data={value}
          analyticsContext={{
            ...analyticsContext,
            sourceSection: nested ? analyticsContext?.sourceSection || "content_body" : "official_links",
          }}
        />
      </div>
    );
  }

  if (isPrimitive(value)) {
    if (!nested) {
      return (
          <div className="job-inline-row">
            <strong>{formatLabel(title)}:</strong>{" "}
            <PrimitiveValue
              value={value}
              fieldKey={title}
              analyticsContext={analyticsContext}
            />
          </div>
        );
    }

    return (
      <div className={sectionClass}>
        {titleNode}
        <div className="job-copy-block">
          <PrimitiveValue value={value} fieldKey={title} analyticsContext={analyticsContext} />
        </div>
      </div>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 1 && !isEmptyValue(value[0])) {
      const singleItem = value[0];

      if (isPrimitive(singleItem)) {
        return (
          <div className="job-inline-row">
            <strong>{formatLabel(title)}:</strong>{" "}
            <PrimitiveValue
              value={singleItem}
              fieldKey={title}
              analyticsContext={analyticsContext}
            />
          </div>
        );
      }

      const singleEntry = getSingleObjectEntry(singleItem);
      if (singleEntry) {
        const [childKey, childValue] = singleEntry;
        return (
          <div className="job-inline-row">
            <strong>{formatLabel(title)}:</strong>{" "}
            <ValueRenderer
              value={{ [childKey]: childValue }}
              fieldKey={title}
              compact
              analyticsContext={analyticsContext}
            />
          </div>
        );
      }
    }

    const objectItems = value.filter((item) => isObject(item));
    return (
      <div className={sectionClass}>
        {titleNode}
        {objectItems.length === value.length ? (
          <ObjectRowsTable rows={objectItems} analyticsContext={analyticsContext} />
        ) : (
          <BulletList items={value} fieldKey={title} analyticsContext={analyticsContext} />
        )}
      </div>
    );
  }

  return (
    <div className={sectionClass}>
      {titleNode}
      <ValueRenderer value={value} fieldKey={title} analyticsContext={analyticsContext} />
    </div>
  );
}

export function JobDetailsView({
  job,
  onBack,
  renderSectionActions,
  canonicalUrl = "",
  showHelmet = true,
  extraActions = null,
  pageTypeOverride = "",
}) {
  const data = useMemo(() => normalizeJobPayload(job), [job]);
  const title = data?.title || "Untitled Job";
  const advertisementNo = data?.advertisementNo || data?.advertisement_no || "NA";
  const postName = data?.postName || data?.post_name || "NA";
  const description = buildJobMetaDescription(data);
  const source = data?.source || "";
  const sections = useMemo(() => buildOrderedSections(data), [data]);
  const jobSupportContent = useMemo(
    () => buildJobSupportContent(data, title, postName),
    [data, postName, title]
  );
  const seoTitle = useMemo(() => buildJobSeoTitle(data), [data]);
  const badgeItems = useMemo(() => buildJobBadgeItems(data), [data]);
  const faqEntries = useMemo(() => buildJobFaqEntries(data), [data]);
  const resolvedCanonicalUrl = useMemo(
    () => canonicalUrl || buildCanonicalUrl(`/job/${getJobSlug(data)}`),
    [canonicalUrl, data]
  );
  const structuredData = useMemo(
    () => buildJobStructuredData(data, resolvedCanonicalUrl),
    [data, resolvedCanonicalUrl]
  );
  const seoMeta = useMemo(() => buildJobSeoMeta(data), [data]);
  const statusLabel = useMemo(() => getJobStatusLabel(data), [data]);
  const pageType = useMemo(() => {
    if (pageTypeOverride) return pageTypeOverride;

    const detectedStatus = detectJobStatus(data);
    if (detectedStatus === "result") return "result_detail";
    if (detectedStatus === "admit-card") return "admit_card_detail";
    return getPageType(typeof window !== "undefined" ? window.location.pathname : "");
  }, [data, pageTypeOverride]);
  const analyticsContext = useMemo(
    () => ({
      pageType,
      itemTitle: title,
      examName: data?.examName || data?.exam_name || title,
      organizationName: data?.conductingBody || data?.conducting_body || data?.organization || "",
      postName: postName !== "NA" ? postName : title,
      categoryType: statusLabel,
      sourceSection: "content_body",
    }),
    [data, pageType, postName, statusLabel, title]
  );

  return (
    <>
      {showHelmet ? (
        <Helmet>
          <title>{`${seoTitle} | AAMSchool`}</title>
          <link rel="canonical" href={resolvedCanonicalUrl} />
          <meta name="description" content={description} />
          <meta property="og:title" content={seoTitle} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content={OG_IMAGE_URL} />
          <meta property="og:url" content={resolvedCanonicalUrl} />
          <meta property="og:type" content="article" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seoTitle} />
          <meta name="twitter:description" content={description} />
          <script type="application/ld+json">{JSON.stringify(structuredData.faqSchema)}</script>
          <script type="application/ld+json">{JSON.stringify(structuredData.jobPostingSchema)}</script>
          <script type="application/ld+json">{JSON.stringify(structuredData.breadcrumbSchema)}</script>
        </Helmet>
      ) : null}

      <article className="job-details-container">
        {onBack ? (
          <button type="button" className="back-button" onClick={onBack}>
            Back
          </button>
        ) : null}

        <div className="job-card">
          <div className="job-header">
            <nav className="job-breadcrumbs" aria-label="Breadcrumb">
              <span>Home</span>
              <span>/</span>
              <span>Jobs</span>
              <span>/</span>
              <span>{statusLabel}</span>
            </nav>
            <h1>{title}</h1>
            <p>
              <strong>Advt. No:</strong> {advertisementNo}
              {" | "}
              <strong>Post:</strong> {postName}
            </p>
            <div className="job-badge-row" aria-label="Job status highlights">
              {badgeItems.length ? (
                badgeItems.map((item) => (
                  <div key={item.label} className={`job-badge job-badge-${item.tone}`}>
                    <span>{item.icon}</span>
                    <strong>{item.label}</strong>
                    <span>{item.value}</span>
                  </div>
                ))
              ) : (
                <div className="job-badge job-badge-neutral">
                  <strong>Status</strong>
                  <span>{statusLabel}</span>
                </div>
              )}
            </div>
            {source && isUrl(source) ? (
              <div className="job-source-link">
                <LinkValue
                  value={source}
                  fieldKey="source"
                  analyticsContext={{ ...analyticsContext, sourceSection: "hero_section" }}
                />
              </div>
            ) : null}
            {extraActions ? <div className="job-source-link">{extraActions}</div> : null}
          </div>

          <div className="job-section">
            <div className="job-overview-grid">
              <div className="job-overview-card">
                <span>Post Name</span>
                <strong>{postName}</strong>
              </div>
              <div className="job-overview-card">
                <span>Advertisement No.</span>
                <strong>{advertisementNo}</strong>
              </div>
              <div className="job-overview-card">
                <span>Official Source</span>
                <strong>{source && isUrl(source) ? "Available" : "Check links below"}</strong>
              </div>
              <div className="job-overview-card">
                <span>Posted on</span>
                <strong>{seoMeta.datePostedLabel || "Not available"}</strong>
              </div>
              <div className="job-overview-card">
                <span>Job Location</span>
                <strong>{seoMeta.locationText || "India"}</strong>
              </div>
              <div className="job-overview-card">
                <span>Employment Type</span>
                <strong>{seoMeta.employmentType ? seoMeta.employmentType.replace(/_/g, " ") : "FULL TIME"}</strong>
              </div>
              <div className="job-overview-card">
                <span>Salary / Pay Scale</span>
                <strong>{seoMeta.salaryText || "Not announced"}</strong>
              </div>
            </div>

            <div className="job-support-card">
              <h3>Job Overview</h3>
              <div className="job-support-list">
                {jobSupportContent.overviewPoints.map((item) => (
                  <p key={item}>{item}</p>
                ))}
              </div>
            </div>

            {description !== "NA" ? (
              <div className="job-inline-row job-description-row">
                <strong>Description:</strong> <span>{description}</span>
              </div>
            ) : null}
            <div className="job-support-card job-seo-facts-card">
              <h3>Key Job Facts</h3>
              <div className="job-inline-list">
                <div className="job-inline-row">
                  <strong>Posted on:</strong> <span>{seoMeta.datePostedLabel || "Not available"}</span>
                </div>
                <div className="job-inline-row">
                  <strong>Job Location:</strong> <span>{seoMeta.locationText || "India"}</span>
                </div>
                <div className="job-inline-row">
                  <strong>Employment Type:</strong>{" "}
                  <span>{seoMeta.employmentType ? seoMeta.employmentType.replace(/_/g, " ") : "FULL TIME"}</span>
                </div>
                <div className="job-inline-row">
                  <strong>Salary / Pay Scale:</strong> <span>{seoMeta.salaryText || "Not announced"}</span>
                </div>
              </div>
            </div>
            {sections.map(([key, value]) => (
              <div key={key} className="job-admin-section-shell">
                <SectionBlock title={key} value={value} analyticsContext={analyticsContext} />
                {renderSectionActions ? (
                  <div className="job-admin-section-actions">
                    {renderSectionActions({ key, value, data })}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        <div className="job-support-layout">
          <section className="job-support-card" aria-label="Application checklist">
            <h3>Before You Apply</h3>
            <ul className="job-support-bullets">
              {jobSupportContent.checklist.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="job-support-card" aria-label="Candidate reminders">
            <h3>Important Reminders</h3>
            <ul className="job-support-bullets">
              {jobSupportContent.reminders.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        </div>

        <section className="job-support-card job-faq-shell" aria-label="Job post FAQ">
          <h3>{`${postName || "Job"} FAQ`}</h3>
          <div className="job-faq-list">
            {faqEntries.map((item) => (
              <article key={item.question} className="job-faq-item">
                <h4>{item.question}</h4>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </section>
      </article>
    </>
  );
}

export default function JsonToTableFinalClean() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { slugWithId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isSyllabusDoc = location.state?.type === "syllabus-doc";

  useEffect(() => {
    const controller = new AbortController();

    const loadJob = async () => {
      try {
        setLoading(true);
        setError("");
        const job = await masterJobsReadApi.getBySlug(slugWithId);
        if (controller.signal.aborted) return;
        setData(normalizeJobPayload(job));
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err.message || "Unable to load job details.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadJob();
    return () => controller.abort();
  }, [slugWithId]);

  const canonicalUrl = "";

  if (loading) {
    return <div className="job-details-container">Loading...</div>;
  }

  if (error) {
    return (
      <div className="job-details-container">
        <p className="job-error">Error: {error}</p>
        <button type="button" className="back-button" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>
    );
  }

  return (
    <JobDetailsView
      job={data}
      canonicalUrl={canonicalUrl}
      onBack={() => navigate(-1)}
      pageTypeOverride={isSyllabusDoc ? "exam_page" : ""}
      extraActions={
        isSyllabusDoc ? (
          <Link
            className="link-btn"
            to={`/syllabus/${slugWithId}`}
            onClick={() =>
              trackCtaClick({
                eventName: "syllabus_click",
                label: "Download Syllabus",
                destinationUrl: `/syllabus/${slugWithId}`,
                page_type: "job_detail",
                item_title: data?.title || "",
                exam_name: data?.examName || data?.exam_name || data?.title || "",
                organization_name:
                  data?.conductingBody || data?.conducting_body || data?.organization || "",
                post_name: data?.postName || data?.post_name || data?.title || "",
                category_type: getJobStatusLabel(data),
                source_section: "top_cta",
              })
            }
          >
            Download Syllabus
          </Link>
        ) : null
      }
    />
  );
}
